import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './QuickProfileQuestions.css';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const QuickProfileQuestions = ({ userType, onComplete, onBack }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    club: '',
    experience: '',
    specialization: '',
    additionalInfo: ''
  });
  const [errors, setErrors] = useState({});

  const getQuestionsForUserType = (type) => {
    switch (type) {
      case 'club_admin':
        return [
          {
            id: 'firstName',
            label: t('firstName'),
            placeholder: t('enterFirstName'),
            icon: <PersonIcon className="question-icon" />,
            required: true
          },
          {
            id: 'lastName',
            label: t('lastName'),
            placeholder: t('enterLastName'),
            icon: <PersonIcon className="question-icon" />,
            required: true
          },
          {
            id: 'email',
            label: t('email'),
            placeholder: t('enterEmail'),
            icon: <EmailIcon className="question-icon" />,
            type: 'email',
            required: true
          },
          {
            id: 'phone',
            label: t('phone'),
            placeholder: t('enterPhone'),
            icon: <PhoneIcon className="question-icon" />,
            type: 'tel',
            required: true
          },
          {
            id: 'club',
            label: t('clubName'),
            placeholder: t('enterClubName'),
            icon: <LocationOnIcon className="question-icon" />,
            required: true
          }
        ];
      case 'coach':
        return [
          {
            id: 'firstName',
            label: t('firstName'),
            placeholder: t('enterFirstName'),
            icon: <PersonIcon className="question-icon" />,
            required: true
          },
          {
            id: 'lastName',
            label: t('lastName'),
            placeholder: t('enterLastName'),
            icon: <PersonIcon className="question-icon" />,
            required: true
          },
          {
            id: 'email',
            label: t('email'),
            placeholder: t('enterEmail'),
            icon: <EmailIcon className="question-icon" />,
            type: 'email',
            required: true
          },
          {
            id: 'phone',
            label: t('phone'),
            placeholder: t('enterPhone'),
            icon: <PhoneIcon className="question-icon" />,
            type: 'tel',
            required: true
          },
          {
            id: 'club',
            label: t('clubName'),
            placeholder: t('enterClubName'),
            icon: <LocationOnIcon className="question-icon" />,
            required: true
          },
          {
            id: 'experience',
            label: t('coachingExperience'),
            placeholder: t('enterCoachingExperience'),
            icon: <WorkIcon className="question-icon" />,
            type: 'select',
            options: [
              { value: '0-1', label: t('lessThan1Year') },
              { value: '1-3', label: t('1to3Years') },
              { value: '3-5', label: t('3to5Years') },
              { value: '5-10', label: t('5to10Years') },
              { value: '10+', label: t('moreThan10Years') }
            ],
            required: true
          },
          {
            id: 'specialization',
            label: t('specialization'),
            placeholder: t('enterSpecialization'),
            icon: <SportsTennisIcon className="question-icon" />,
            required: false
          }
        ];
      case 'player':
        return [
          {
            id: 'firstName',
            label: t('firstName'),
            placeholder: t('enterFirstName'),
            icon: <PersonIcon className="question-icon" />,
            required: true
          },
          {
            id: 'lastName',
            label: t('lastName'),
            placeholder: t('enterLastName'),
            icon: <PersonIcon className="question-icon" />,
            required: true
          },
          {
            id: 'email',
            label: t('email'),
            placeholder: t('enterEmail'),
            icon: <EmailIcon className="question-icon" />,
            type: 'email',
            required: true
          },
          {
            id: 'phone',
            label: t('phone'),
            placeholder: t('enterPhone'),
            icon: <PhoneIcon className="question-icon" />,
            type: 'tel',
            required: false
          },
          {
            id: 'club',
            label: t('clubName'),
            placeholder: t('enterClubName'),
            icon: <LocationOnIcon className="question-icon" />,
            required: false
          },
          {
            id: 'experience',
            label: t('playingExperience'),
            placeholder: t('enterPlayingExperience'),
            icon: <SportsTennisIcon className="question-icon" />,
            type: 'select',
            options: [
              { value: 'beginner', label: t('beginner') },
              { value: 'intermediate', label: t('intermediate') },
              { value: 'advanced', label: t('advanced') },
              { value: 'professional', label: t('professional') }
            ],
            required: true
          }
        ];
      default:
        return [];
    }
  };

  const questions = getQuestionsForUserType(userType);

  const getUserTypeIcon = (type) => {
    switch (type) {
      case 'club_admin':
        return <AdminPanelSettingsIcon className="user-type-icon" />;
      case 'coach':
        return <SportsTennisIcon className="user-type-icon" />;
      case 'player':
        return <PersonIcon className="user-type-icon" />;
      default:
        return <PersonIcon className="user-type-icon" />;
    }
  };

  const getUserTypeTitle = (type) => {
    switch (type) {
      case 'club_admin':
        return t('clubAdminProfile');
      case 'coach':
        return t('coachProfile');
      case 'player':
        return t('playerProfile');
      default:
        return t('userProfile');
    }
  };

  const handleInputChange = (id, value) => {
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
    
    // Clear error when user starts typing
    if (errors[id]) {
      setErrors(prev => ({
        ...prev,
        [id]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    questions.forEach(question => {
      if (question.required && !formData[question.id]) {
        newErrors[question.id] = t('fieldRequired');
      }
      
      if (question.type === 'email' && formData[question.id]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[question.id])) {
          newErrors[question.id] = t('invalidEmail');
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onComplete({
        userType,
        ...formData
      });
    }
  };

  const renderInput = (question) => {
    if (question.type === 'select') {
      return (
        <select
          className={`question-input ${errors[question.id] ? 'error' : ''}`}
          value={formData[question.id]}
          onChange={(e) => handleInputChange(question.id, e.target.value)}
        >
          <option value="">{question.placeholder}</option>
          {question.options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={question.type || 'text'}
        className={`question-input ${errors[question.id] ? 'error' : ''}`}
        placeholder={question.placeholder}
        value={formData[question.id]}
        onChange={(e) => handleInputChange(question.id, e.target.value)}
      />
    );
  };

  return (
    <div className="quick-profile-container">
      <div className="quick-profile-card">
        <div className="quick-profile-header">
          <div className="user-type-indicator">
            {getUserTypeIcon(userType)}
            <h1 className="quick-profile-title">{getUserTypeTitle(userType)}</h1>
          </div>
          <p className="quick-profile-subtitle">{t('quickProfileDescription')}</p>
        </div>

        <form onSubmit={handleSubmit} className="quick-profile-form">
          <div className="questions-grid">
            {questions.map((question) => (
              <div key={question.id} className="question-group">
                <label className="question-label">
                  <span className="question-icon-container">
                    {question.icon}
                  </span>
                  {question.label}
                  {question.required && <span className="required">*</span>}
                </label>
                {renderInput(question)}
                {errors[question.id] && (
                  <span className="error-message">{errors[question.id]}</span>
                )}
              </div>
            ))}
          </div>

          <div className="quick-profile-actions">
            {onBack && (
              <button 
                type="button" 
                className="back-button"
                onClick={onBack}
              >
                {t('back')}
              </button>
            )}
            <button
              type="submit"
              className="continue-button"
            >
              {t('continue')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickProfileQuestions;


